import type { ParameterOption, ParameterRange, RangeBound } from '@shared/types';

export type NumberParseResult = {
  type: 'number' | 'angle';
  value: number;
  range?: ParameterRange;
  options?: ParameterOption[];
  unit?: string;
};

/**
 * Detects a bare number value and parses associated range/options/angle hints
 * from the inline comment.
 *
 * Handles:
 * - `[value:Label, ...]` labelled dropdown syntax
 * - `[opt1, opt2, ...]` plain options list
 * - `val=Label, ...` key=value options
 * - `[min:max]` / `[min:step:max]` / `[outerMin .. [safe] .. outerMax]` range syntax
 * - Degree symbol `°` to upgrade type to 'angle'
 * - `min=`, `max=`, `step=` keyword notation
 *
 * Returns null if valueStr is not a bare number.
 */
export function parseNumberType(
  valueStr: string,
  comment: string,
): NumberParseResult | null {
  if (isNaN(Number(valueStr))) return null;

  const value = Number(valueStr);
  let type: 'number' | 'angle' = 'number';
  let range: ParameterRange | undefined = undefined;
  let options: ParameterOption[] | undefined = undefined;

  if (comment) {
    // --- Check for [value:Label, value:Label, ...] bracket labelled dropdown ---
    const bracketLabelledMatch = comment.match(/\[([^\]]+)\]/);
    if (bracketLabelledMatch) {
      const inner = bracketLabelledMatch[1];
      const tokens = inner
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== '');
      // Each token must be "numericValue:Label" where the label is non-numeric
      // and contains no further colons (to avoid matching range syntax like [5:200])
      const isLabelledList =
        tokens.length > 0 &&
        tokens.every((tok) => {
          const colonIdx = tok.indexOf(':');
          if (colonIdx === -1) return false;
          const valPart = tok.slice(0, colonIdx).trim();
          const labelPart = tok.slice(colonIdx + 1).trim();
          return (
            !isNaN(Number(valPart)) &&
            isNaN(Number(labelPart)) &&
            labelPart.length > 0 &&
            !labelPart.includes(':')
          );
        });
      if (isLabelledList) {
        options = tokens.map((tok) => {
          const colonIdx = tok.indexOf(':');
          const valPart = tok.slice(0, colonIdx).trim();
          const labelPart = tok.slice(colonIdx + 1).trim();
          return { value: Number(valPart), label: labelPart };
        });
      }
    }

    // --- Plain options list: [opt1, opt2, ...] ---
    if (!options) {
      const optionsMatch = comment.match(/\[([^\]:]+)\]/);
      if (optionsMatch && !optionsMatch[1].includes(':')) {
        const rawOptions = optionsMatch[1]
          .split(',')
          .map((s) => s.replace(/\/\/.*$/, '').trim())
          .filter((s) => s !== '');
        options = rawOptions.map((opt) => ({
          label: opt,
          value: isNaN(Number(opt)) ? opt : Number(opt),
        }));
      }
    }

    // --- key=value options: "0=Label, 1=Other" ---
    if (!options && comment.includes('=')) {
      const pairs = comment.split(',').map((s) => s.trim());
      const extractedOptions: ParameterOption[] = [];
      for (const pair of pairs) {
        const parts = pair.split('=').map((s) => s.trim());
        if (parts.length === 2) {
          const val = Number(parts[0]);
          if (!isNaN(val)) {
            extractedOptions.push({ value: val, label: parts[1] });
          } else {
            const strVal = parts[0].replace(/^"|"$/g, '');
            extractedOptions.push({ value: strVal, label: parts[1] });
          }
        }
      }
      if (extractedOptions.length > 0) {
        options = extractedOptions;
      }
    }
  }

  // --- Range parsing (min/max/step) ---
  // Bounds may be literal numbers or $-prefixed parameter references.
  if (comment) {
    const NR = '(-?\\d+(?:\\.\\d+)?|\\$[a-zA-Z_][a-zA-Z0-9_]*)';
    const parseBound = (raw: string): RangeBound =>
      raw.startsWith('$') ? raw : Number(raw);

    // Keyword notation: min=/max=/step= — extended to accept $-references
    const minMatch = comment.match(new RegExp(`min[:=]\\s*${NR}`, 'i'));
    const maxMatch = comment.match(new RegExp(`max[:=]\\s*${NR}`, 'i'));
    const stepMatch = comment.match(new RegExp(`step[:=]\\s*${NR}`, 'i'));

    if (minMatch || maxMatch || stepMatch) {
      range = range || {};
      if (minMatch) range.min = parseBound(minMatch[1]);
      if (maxMatch) range.max = parseBound(maxMatch[1]);
      if (stepMatch) range.step = parseBound(stepMatch[1]);
    }

    // Bracket notation for range
    const hasDegree = comment.includes('°');
    const NR_DEG = '(-?\\d+(?:\\.\\d+)?°?|\\$[a-zA-Z_][a-zA-Z0-9_]*)';
    const activeToken = hasDegree ? NR_DEG : NR;
    const parseBoundDeg = (raw: string): RangeBound =>
      raw.startsWith('$') ? raw : Number(raw.replace('°', ''));
    const activeParseBound = hasDegree ? parseBoundDeg : parseBound;

    // Extended safe-range syntax: [outerMin .. [safeMin:step:safeMax] .. outerMax]
    const extendedMatch = comment.match(
      new RegExp(
        `\\[\\s*${activeToken}\\s*\\.\\.\\s*\\[\\s*${activeToken}\\s*(?::\\s*${activeToken})?\\s*:\\s*${activeToken}\\s*\\]\\s*\\.\\.\\s*${activeToken}\\s*\\]`,
      ),
    );
    if (extendedMatch) {
      range = range || {};
      range.min = activeParseBound(extendedMatch[1]);
      range.safeMin = activeParseBound(extendedMatch[2]);
      const maybeStep = extendedMatch[3]
        ? activeParseBound(extendedMatch[3])
        : undefined;
      range.safeMax = activeParseBound(extendedMatch[4]);
      range.max = activeParseBound(extendedMatch[5]);
      if (maybeStep !== undefined) {
        range.step = maybeStep;
      }
      if (hasDegree) type = 'angle';
    } else {
      // Standard bracket notation: [min:step:max] or [min:max]
      const bracketMatch = comment.match(
        new RegExp(
          `\\[\\s*${activeToken}\\s*(?::\\s*${activeToken})?\\s*:\\s*${activeToken}\\s*\\]`,
        ),
      );
      if (bracketMatch) {
        range = range || {};
        const firstVal = activeParseBound(bracketMatch[1]);
        const secondVal = bracketMatch[2]
          ? activeParseBound(bracketMatch[2])
          : undefined;
        const thirdVal = activeParseBound(bracketMatch[3]);

        range.min = firstVal;
        if (secondVal !== undefined) {
          range.step = secondVal;
          range.max = thirdVal;
        } else {
          range.max = thirdVal;
        }
        // Upgrade to angle type when any token contained °
        if (
          hasDegree &&
          (bracketMatch[1].includes('°') ||
            bracketMatch[2]?.includes('°') ||
            bracketMatch[3].includes('°'))
        ) {
          type = 'angle';
        }
      }
    }
  }

  // --- Unit label: unit=mm / unit=% / unit= (empty suppresses heuristic) ---
  let unit: string | undefined = undefined;
  if (comment) {
    const unitMatch = comment.match(/\bunit[:=]\s*([^\s,\]]*)/i);
    if (unitMatch) {
      unit = unitMatch[1].trim();
    }
  }

  return { type, value, range, options, unit };
}
